import { useEffect, useState } from 'react'
import { Mail, Phone, User, UserPlus } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import ContactsService, { type Contact } from '../../services/contacts'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'

/**
 * LOT-CONTACT-LINK — création d'un contact DEPUIS la qualification.
 *
 * Constat utilisateur : un numéro composé à la main qui n'appartient à aucune
 * fiche reste anonyme pour toujours — l'agent n'avait aucun moyen de créer le
 * contact sans quitter son appel. Ce modal est ouvert par le bouton `UserPlus`
 * de `CallOutcomeModal` (visible uniquement quand l'appel n'a pas de contact,
 * et gated par la permission `canCreateContacts`).
 *
 * Le téléphone arrive PRÉREMPLI (`initialPhone` = numéro de l'appel en cours) :
 * c'est lui qui garantit que la fiche créée porte bien le numéro composé. Au
 * succès, `onCreated` remonte le contact complet au parent — qui affiche le nom
 * et transmet le `contactId` avec l'outcome (rattachement serveur, cf.
 * `SetOutcomeDto.contactId`).
 *
 * #239 — sur le kit `Modal` (calque `MODAL_Z_INDEX`, `role="dialog"` +
 * `aria-modal`, piège de focus, Échap, pied toujours visible). Le kit portale
 * sur `document.body` : les requêtes des specs se font donc sur `document`,
 * jamais sur le `container` de render (piège documenté au registre).
 *
 * ⚠️ EMPILEMENT : le kit est au calque 9990, plus bas que l'ancien `z-[9999]`
 * en dur. Deux parents l'ouvrent : `CallOutcomeModal` (passée au même calque
 * par #239 — sans quoi ce formulaire s'ouvrirait SOUS la qualification) et
 * `UnknownNumberPanel`, qui est elle-même une `Modal` et se met en retrait
 * pendant l'ouverture (deux pièges de focus ne cohabitent pas).
 */

interface CreateContactModalProps {
  isOpen: boolean
  onClose: () => void
  /** Numéro de l'appel en cours — préremplit le champ téléphone. */
  initialPhone?: string
  /** Appelé au succès avec le contact créé (id + nom pour l'affichage). */
  onCreated: (contact: Contact) => void
}

const CreateContactModal = ({
  isOpen,
  onClose,
  initialPhone,
  onCreated,
}: CreateContactModalProps) => {
  const { t } = useTranslation('contacts')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState(initialPhone ?? '')
  const [email, setEmail] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  // LOT-ANTI-DOUBLON — fiche existante remontée par le 409 DUPLICATE_PHONE du
  // backend : on propose de la RÉUTILISER (même effet qu'une création : le
  // contact est rattaché à la qualification) plutôt qu'une erreur sèche.
  const [duplicate, setDuplicate] = useState<{ id: string; name: string } | null>(null)

  // Formulaire remis à zéro à chaque ouverture : un modal rouvert sur un autre
  // appel ne doit jamais montrer la saisie du précédent.
  useEffect(() => {
    if (isOpen) {
      setName('')
      setPhone(initialPhone ?? '')
      setEmail('')
      setIsSaving(false)
      setDuplicate(null)
    }
  }, [isOpen, initialPhone])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error(t('createModal.nameRequired'))
      return
    }
    if (!phone.trim()) {
      toast.error(t('createModal.phoneRequired'))
      return
    }
    try {
      setIsSaving(true)
      const contact = await ContactsService.create({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
      })
      toast.success(t('createModal.created'), { duration: 2000 })
      onCreated(contact)
      onClose()
    } catch (error: any) {
      const data = error?.response?.data
      if (
        error?.response?.status === 409 &&
        data?.code === 'DUPLICATE_PHONE' &&
        data?.existing?.id
      ) {
        // Doublon : encart dans le modal (pas de toast) — l'agent choisit
        // entre reprendre la fiche existante et corriger le numéro.
        setDuplicate({ id: data.existing.id, name: data.existing.name })
      } else {
        toast.error(
          data?.message || error?.message || t('createModal.createFailed'),
        )
      }
    } finally {
      setIsSaving(false)
    }
  }

  /** « Utiliser cette fiche » — même effet qu'une création réussie. */
  const useExisting = () => {
    if (!duplicate) return
    onCreated({ id: duplicate.id, name: duplicate.name, phone: phone.trim() } as Contact)
    onClose()
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={t('createModal.title')}
      subtitle={t('createModal.subtitle')}
      icon={UserPlus}
      width="sm"
      busy={isSaving}
      closeLabel={t('createModal.cancel')}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1"
          >
            {t('createModal.cancel')}
          </Button>
          <Button variant="primary" type="submit" loading={isSaving} className="flex-1">
            {isSaving ? t('createModal.submitting') : t('createModal.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Nom (requis) — le kit place le focus d'ouverture sur le premier champ. */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground-secondary">
            {t('createModal.nameLabel')} *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-secondary" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input pl-10"
              placeholder={t('createModal.namePlaceholder')}
            />
          </div>
        </div>

        {/* Téléphone (requis, prérempli avec le numéro composé) */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground-secondary">
            {t('createModal.phoneLabel')} *
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-secondary" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value)
                // Le numéro change : l'encart de doublon ne parle plus de lui.
                setDuplicate(null)
              }}
              className="input pl-10"
              placeholder={t('createModal.phonePlaceholder')}
            />
          </div>
        </div>

        {/* LOT-ANTI-DOUBLON — le numéro appartient déjà à une fiche : on
            propose de la reprendre (rattachée à la qualification comme une
            création) ou de corriger le numéro saisi. */}
        {duplicate && (
          <div
            role="alert"
            className="space-y-2 rounded-lg border border-warning-500/40 bg-warning-500/10 p-3"
          >
            <p className="text-sm text-foreground">
              {t('createModal.duplicateNotice', { name: duplicate.name })}
            </p>
            <div className="flex gap-2">
              <Button
                variant="primary"
                type="button"
                size="sm"
                onClick={useExisting}
                className="flex-1"
              >
                {t('createModal.duplicateUse')}
              </Button>
              <Button
                variant="secondary"
                type="button"
                size="sm"
                onClick={() => setDuplicate(null)}
                className="flex-1"
              >
                {t('createModal.duplicateFix')}
              </Button>
            </div>
          </div>
        )}

        {/* Email (optionnel) */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground-secondary">
            {t('createModal.emailLabel')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-secondary" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input pl-10"
              placeholder={t('createModal.emailPlaceholder')}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default CreateContactModal
